import './styles.css';

import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { observer, inject } from 'mobx-react';
import { observable, action } from 'mobx';
import Grid from '@material-ui/core/Grid';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import PictureAsPdf from '@material-ui/icons/PictureAsPdf';
import { Header } from '../Header/Header';
import { Loader } from '../Loader/Loader';
import tracker from '../Loader/loader-tracker';
import { ActionsMenu } from '../ActionsMenu/ActionsMenu';
import { ActionItem } from '../ActionsMenu/ActionItem';
import { SalesStore } from '../../stores/sales-store';
import { UiStore } from '../../stores/ui-store';
import { OrderUiState } from '../../stores/ui-state/order-ui-state';
import { Order, OrderWorkflow } from '../../models/order';
import { Refund } from '../../models/refund';
import AddressSearchBox from '../AddressSearch/AddressSearchBox';
import AddressMap from '../AddressSearch/AddressMap';
import { Address } from '../../models/address';
import CreditRequestProductLines from './CreditRequestProductLines';
import CurrencyField, { ICurrencyChangedEvent } from '../Currency/CurrencyField';
import TextField from '@material-ui/core/TextField';
import OrderSummary from '../Order/OrderSummary';
import { dateFormatted } from '../../datetime';
import { ProductLine } from '../../models/product-line';
import { round } from '../../math';
import type {
    ICreditRequestPdfRequest,
    ICreditRequestPdfField,
    ICreditRequestPdfProductLine
} from '../../services/sales-api';

export interface ICreditRequestProps {
    salesStore?: SalesStore;
    uiStore?: UiStore;
}

interface MatchParams {
    id: string;
}

interface IProductRestockingFee {
    applied: boolean;
    percentage: number;
}

type RequiredYesNo = boolean | null;
type FreightChargeMode = '' | 'deduct' | 'separate';

type Props = ICreditRequestProps & RouteComponentProps<MatchParams>;

@inject('salesStore', 'uiStore')
@observer
class CreditRequest extends React.Component<Props> {
    private salesStore: SalesStore;
    private uiStore: UiStore;
    private orderUiState: OrderUiState;

    @observable private reason: string = '';
    @observable private customerCreditRequired: RequiredYesNo = null;
    @observable private returnRequired: RequiredYesNo = null;
    @observable private freightRequired: RequiredYesNo = null;
    @observable private freightChargeMode: FreightChargeMode = '';
    @observable private freightChargeTo: string = '';
    @observable private freightAmount: number = 0;
    @observable private freightMojoeNumber: string = '';
    @observable private freightNoReason: string = '';
    @observable private newPurchaseOrderRequired: RequiredYesNo = null;
    @observable private newPurchaseOrderNoCharge: RequiredYesNo = null;
    @observable private supplierContacted: RequiredYesNo = null;
    @observable private supplierName: string = '';
    @observable private supplierContactName: string = '';
    @observable private supplierCreditRequired: RequiredYesNo = null;
    @observable private supplierCreditAmount: number = 0;
    @observable private supplierRestockingFeeRequired: RequiredYesNo = null;
    @observable private supplierRestockingFeeAmount: number = 0;
    @observable private fixedAmountCreditExGst: number = 0;
    @observable private cashCustomerNameOfAccount: string = '';
    @observable private cashCustomerBsb: string = '';
    @observable private cashCustomerAccountNumber: string = '';
    @observable private cashCustomerAccountCheckedVerbally: RequiredYesNo = null;
    @observable private cashCustomerAccountCheckedBy: string = '';
    @observable private productRestockingFees: { [key: string]: IProductRestockingFee } = {};

    constructor(props: Props) {
        super(props);
        this.salesStore = props.salesStore!;
        this.uiStore = props.uiStore!;
        this.orderUiState = props.uiStore!.orderUiState;
    }

    public async componentDidMount() {
        this.orderUiState.setFormReadOnly(true);
        const order = await tracker.track(this.salesStore.loadOrder(this.props.match.params.id));
        this.orderUiState.initViewOrder(order);
        this.orderUiState.setFormReadOnly(false);
        this.startCreditRequest(order);
    }

    @action
    private startCreditRequest(order: Order) {
        order.setOrderWorkflow(OrderWorkflow.RefundingOrder);
        order.refund = new Refund(order);
        order.refund.update('fromAddress', this.copyAddress(order.delivery.address));
    }

    public componentWillUnmount() {
        this.salesStore.order.setOrderWorkflow(OrderWorkflow.None);
        this.orderUiState.setFormReadOnly(true);
    }

    @action
    private setReason(reason: string) {
        this.reason = reason;
    }

    @action
    private setFreightChargeMode(mode: FreightChargeMode) {
        this.freightChargeMode = mode;
        if (mode === 'deduct') this.freightChargeTo = 'Take from refund';
        if (mode === 'separate') {
            this.freightChargeTo = 'Charged on separate docket';
            this.freightAmount = 0;
        }
    }

    @action
    private setFreightAmount(amount: number) {
        this.freightAmount = amount;
    }

    @action
    private setFreightMojoeNumber(mojoeNumber: string) {
        this.freightMojoeNumber = mojoeNumber;
    }

    @action
    private setFreightNoReason(reason: string) {
        this.freightNoReason = reason;
    }

    @action
    private setSupplierCreditRequired(checked: boolean) {
        this.supplierCreditRequired = checked;
        if (!checked) this.supplierCreditAmount = 0;
    }

    @action
    private setSupplierCreditAmount(amount: number) {
        this.supplierCreditAmount = amount;
    }

    @action
    private setSupplierRestockingFeeRequired(checked: boolean) {
        this.supplierRestockingFeeRequired = checked;
        if (!checked) this.supplierRestockingFeeAmount = 0;
    }

    @action
    private setSupplierRestockingFeeAmount(amount: number) {
        this.supplierRestockingFeeAmount = amount;
    }

    @action
    private setFixedAmountCreditExGst(e: ICurrencyChangedEvent) {
        this.fixedAmountCreditExGst = round(e.parsedValue);
    }

    @action
    private setCashCustomerNameOfAccount(name: string) {
        this.cashCustomerNameOfAccount = name;
    }

    @action
    private setCashCustomerBsb(bsb: string) {
        this.cashCustomerBsb = bsb;
    }

    @action
    private setCashCustomerAccountNumber(accountNumber: string) {
        this.cashCustomerAccountNumber = accountNumber;
    }

    @action
    private setCashCustomerAccountCheckedVerbally(checked: boolean) {
        this.cashCustomerAccountCheckedVerbally = checked;
    }

    @action
    private setCashCustomerAccountCheckedBy(checkedBy: string) {
        this.cashCustomerAccountCheckedBy = checkedBy;
    }

    @action
    private setNewPurchaseOrderRequired(checked: boolean) {
        this.newPurchaseOrderRequired = checked;
        if (!checked) this.newPurchaseOrderNoCharge = null;
    }

    @action
    private setNewPurchaseOrderNoCharge(checked: boolean) {
        this.newPurchaseOrderNoCharge = checked;
    }

    @action
    private setSupplierContacted(checked: boolean) {
        this.supplierContacted = checked;
        if (!checked) { this.supplierName = ''; this.supplierContactName = ''; }
    }

    @action
    private setSupplierName(name: string) {
        this.supplierName = name;
    }

    @action
    private setSupplierContactName(name: string) {
        this.supplierContactName = name;
    }

    @action
    private setFreightRequired(checked: boolean) {
        this.freightRequired = checked;
        if (!checked) {
            this.freightChargeTo = '';
            this.freightChargeMode = '';
            this.freightAmount = 0;
            this.freightMojoeNumber = '';
        }
        else { this.freightNoReason = ''; }
    }

    @action
    private setCustomerCreditRequired(checked: boolean) {
        this.customerCreditRequired = checked;
    }

    @action
    private setReturnRequired(checked: boolean) {
        const { order } = this.salesStore;
        this.returnRequired = checked;
        order.refund.setPickupRequired(checked);
        if (checked && !order.refund.fromAddress.formattedAddress) {
            order.refund.update('fromAddress', this.copyAddress(order.delivery.address));
        }
    }

    @action
    private setRestockingFeeApplied(productLine: ProductLine, checked: boolean) {
        const key = this.getProductLineKey(productLine);
        this.productRestockingFees = {
            ...this.productRestockingFees,
            [key]: {
                ...this.getProductRestockingFee(productLine),
                applied: checked
            }
        };
    }

    @action
    private setRestockingFeePercentage(productLine: ProductLine, percentage: number) {
        const key = this.getProductLineKey(productLine);
        this.productRestockingFees = {
            ...this.productRestockingFees,
            [key]: {
                ...this.getProductRestockingFee(productLine),
                percentage: Math.min(100, Math.max(0, percentage))
            }
        };
    }

    private getProductLineKey(productLine: ProductLine): string {
        return `${productLine.id}:${productLine.lineNumber}:${productLine.code}`;
    }

    private getProductRestockingFee(productLine: ProductLine): IProductRestockingFee {
        return this.productRestockingFees[this.getProductLineKey(productLine)] || {
            applied: false,
            percentage: 30
        };
    }

    private copyAddress(address: Address): Address {
        return new Address({
            formattedAddress: address.formattedAddress,
            name: address.name,
            street: address.street,
            suburb: address.suburb,
            state: address.state,
            postcode: address.postcode,
            latLng: { ...address.latLng },
            locationType: address.locationType,
            exWorksId: address.exWorksId
        });
    }

    private async downloadCreditRequestPdf() {
        const request = this.getCreditRequestPdfData();
        const blob = await tracker.track(
            this.salesStore.order.downloadCreditRequestPdf(request, this.uiStore.notificationUiState),
            'credit-request'
        );
        if (!blob) return;

        const link = document.createElement('a');
        const href = window.URL.createObjectURL(blob);
        const filename = this.getCreditRequestPdfFilename();

        link.download = filename;
        link.href = href;
        link.target = '_self';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.setTimeout(() => window.URL.revokeObjectURL(href), 0);

        this.uiStore.notificationUiState.showInfo(`Generated credit request ${filename}. Please check your downloads`);
    }

    private formatCurrency(amount: number): string {
        const formattedAmount = Math.abs(amount).toFixed(2);
        return amount < 0 ? `-$${formattedAmount}` : `$${formattedAmount}`;
    }

    private formatQuantity(quantity: number): string {
        if (Number.isInteger(quantity)) return quantity.toString();
        return quantity.toFixed(3).replace(/\.?0+$/, '');
    }

    private yesNo(value: RequiredYesNo): string {
        if (value === null) return 'Not answered';
        return value ? 'Yes' : 'No';
    }

    private getRelatedPurchaseOrderNumbers(productLines: ProductLine[]): string[] {
        return productLines
            .map(productLine => productLine.purchaseOrderNumber)
            .filter((purchaseOrderNumber, index, allPurchaseOrderNumbers) =>
                !!purchaseOrderNumber && allPurchaseOrderNumbers.indexOf(purchaseOrderNumber) === index
            );
    }

    private getDeductedFreightAmount(): number {
        return this.freightRequired && this.freightChargeMode === 'deduct'
            ? this.freightAmount
            : 0;
    }

    private getSupplierRestockingFeeAmount(): number {
        return this.supplierRestockingFeeRequired === true
            ? this.supplierRestockingFeeAmount
            : 0;
    }

    private hasFixedAmountCredit(): boolean {
        return this.fixedAmountCreditExGst !== 0;
    }

    private getCalculatedCreditAmountExGst(productLines: ProductLine[]): number {
        const productReturnValue = this.getTotalProductReturnValue(productLines);
        const restockingFee = Math.max(0, this.getTotalRestockingFee(productLines) + this.getSupplierRestockingFeeAmount());
        const freightAmount = Math.max(0, this.getDeductedFreightAmount());
        return round(productReturnValue - restockingFee - freightAmount);
    }

    private getTotalCreditAmountExGst(calculatedCreditExGst: number): number {
        return this.hasFixedAmountCredit()
            ? round(this.fixedAmountCreditExGst)
            : round(calculatedCreditExGst);
    }

    private getAmountPaid(): number {
        return Math.max(0, this.salesStore.order.actualPaid || 0);
    }

    private returnQuantityIsValid(productLine: ProductLine): boolean {
        if (productLine.errors.creditQuantity) return false;
        return !productLine.creditQuantity ||
            (productLine.creditQuantity > 0 && productLine.creditQuantity <= productLine.quantity);
    }

    private hasInvalidReturnQuantity(): boolean {
        return this.salesStore.order.orderedProductLines
            .some(productLine => !this.returnQuantityIsValid(productLine));
    }

    private getReturnedProductLines(): ProductLine[] {
        return this.salesStore.order.orderedProductLines
            .filter(productLine => productLine.creditQuantity > 0 && this.returnQuantityIsValid(productLine));
    }

    private getProductTotalCredited(productLine: ProductLine): number {
        return this.returnQuantityIsValid(productLine)
            ? productLine.totalCredited
            : 0;
    }

    private getTotalProductReturnValue(productLines: ProductLine[]): number {
        return round(productLines
            .map(productLine => this.getProductTotalCredited(productLine))
            .reduce((total, productTotalCredited) => total + productTotalCredited, 0));
    }

    private renderRequiredYesNo(
        label: string,
        value: RequiredYesNo,
        onChange: (checked: boolean) => void
    ) {
        const required = value === null;
        return (
            <div className="required-yes-no">
                <p className="required-yes-no-label">{label}</p>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={value === true}
                            onChange={() => onChange(true)}
                        />
                    }
                    label="Yes"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={value === false}
                            onChange={() => onChange(false)}
                        />
                    }
                    label="No"
                />
                {required && <p className="required-answer">Select Yes or No.</p>}
            </div>
        );
    }

    private getRestockingFeeAmount(productLine: ProductLine): number {
        const restockingFee = this.getProductRestockingFee(productLine);
        return this.salesStore.order.refund.calculateProductRestockingFee(
            this.getProductTotalCredited(productLine),
            restockingFee.applied,
            restockingFee.percentage
        );
    }

    private getTotalRestockingFee(productLines: ProductLine[]): number {
        const restockingFees = productLines
            .map(productLine => this.getRestockingFeeAmount(productLine));
        return this.salesStore.order.refund.calculateTotalRestockingFee(restockingFees);
    }

    private getProductRefundAmount(productLine: ProductLine): number {
        return this.salesStore.order.refund.calculateProductRefundAmount(
            this.getProductTotalCredited(productLine),
            this.getRestockingFeeAmount(productLine)
        );
    }

    private getOrderSummaryPdfFields(): ICreditRequestPdfField[] {
        const { order } = this.salesStore;
        const fields: ICreditRequestPdfField[] = [];

        if (order.totalWeight > 0) fields.push({ label: 'Weight', value: order.totalWeightFormatted });
        fields.push({
            label: 'Fuel Levy',
            value: order.customerIsFuelLevyExempt ? 'Exempt' : this.formatCurrency(order.fuelLevySurcharge)
        });
        fields.push({ label: 'Subtotal', value: this.formatCurrency(order.subtotal) });
        fields.push({ label: 'Total GP (%)', value: `${order.totalGrossProfit}%` });
        fields.push({ label: 'GST', value: this.formatCurrency(order.gst) });

        if (!order.isSplitOrder && !order.isParentOrder) {
            fields.push({ label: 'Total', value: this.formatCurrency(order.total) });
        }

        if (order.isCashSalesAccount) {
            fields.push({ label: 'Customer Adjustment', value: this.formatCurrency(order.customerAdjustment) });
            if (order.merchantSurchargeRate > 0) {
                fields.push({
                    label: `Merchant Fee (${(order.merchantSurchargeRate * 100).toFixed(2).replace(/\.?0+$/, '')}%)`,
                    value: this.formatCurrency(order.merchantSurcharge)
                });
            }
            if (!order.parentOrder) fields.push({ label: 'Amount Paid', value: this.formatCurrency(order.actualPaid) });
            if (!order.isSplitOrder && !order.isParentOrder) {
                fields.push({ label: 'Balance Due', value: this.formatCurrency(order.balanceDue) });
            }
        }

        if (order.isSplitOrder || order.isParentOrder) {
            fields.push({ label: 'Total of this Order', value: this.formatCurrency(order.total) });
            if (order.isCashSalesAccount && order.parentOrder) {
                fields.push({
                    label: 'Order Balance',
                    value: this.formatCurrency(round(order.total - order.totalPaid))
                });
            }
            if (order.isCashSalesAccount && order.splitOrders && !order.isParentOrder) {
                fields.push({ label: 'Total Payments Allocated', value: this.formatCurrency(order.totalAllocated) });
            }
            if (!(order.isCashSalesAccount && order.parentOrder)) {
                fields.push({ label: 'Grand Total', value: this.formatCurrency(order.grandTotal) });
                fields.push({ label: 'Grand Total Balance Due', value: this.formatCurrency(order.grandTotalBalanceDue) });
            }
            if (order.errors.parentOrderEmpty) {
                fields.push({ label: 'Split order warning', value: order.errors.parentOrderEmpty });
            }
        }

        return fields;
    }

    private getOrderSummaryPdfTitle(): string {
        return this.salesStore.order.isParentOrder ? 'Balance Summary' : 'Summary';
    }

    private getRequestDetailsPdfFields(returnedLines: ProductLine[]): ICreditRequestPdfField[] {
        const { refund } = this.salesStore.order;
        const fields: ICreditRequestPdfField[] = [
            { label: 'Reason', value: this.reason || '-' },
            { label: 'Customer credit required', value: this.yesNo(this.customerCreditRequired) },
            { label: 'Product return required', value: this.yesNo(this.returnRequired) }
        ];

        if (refund.pickupRequired) {
            fields.push({ label: 'Pickup address', value: refund.fromAddress.formattedAddress || '-' });
            fields.push({
                label: 'Products to return',
                value: returnedLines.length > 0
                    ? returnedLines.map(line => `${line.code} x ${line.creditQuantity}`).join(', ')
                    : '-'
            });
        }

        return fields;
    }

    private getFreightDetailsPdfFields(): ICreditRequestPdfField[] {
        const fields: ICreditRequestPdfField[] = [
            { label: 'Freight required', value: this.yesNo(this.freightRequired) }
        ];

        if (this.freightRequired) {
            fields.push({ label: 'Freight charge to', value: this.freightChargeTo || '-' });
            fields.push({ label: 'Freight Mojoe #', value: this.freightMojoeNumber || '-' });
        }

        if (this.freightRequired === false) {
            fields.push({ label: 'No freight reason', value: this.freightNoReason || '-' });
        }

        return fields;
    }

    private getSupplierDetailsPdfFields(): ICreditRequestPdfField[] {
        const fields: ICreditRequestPdfField[] = [];

        fields.push({ label: 'Supplier credit required', value: this.yesNo(this.supplierCreditRequired) });
        if (this.supplierCreditRequired === true) {
            fields.push({
                label: 'Supplier credit amount (ex GST)',
                value: this.formatCurrency(this.supplierCreditAmount)
            });
        }

        fields.push({ label: 'Supplier restocking fee', value: this.yesNo(this.supplierRestockingFeeRequired) });
        if (this.supplierRestockingFeeRequired === true) {
            fields.push({
                label: 'Supplier restocking fee (ex GST)',
                value: this.formatCurrency(this.supplierRestockingFeeAmount)
            });
        }

        fields.push({ label: 'Supplier contacted', value: this.yesNo(this.supplierContacted) });
        if (this.supplierContacted === true) {
            fields.push({ label: 'Supplier', value: this.supplierName || '-' });
            fields.push({ label: 'Supplier contact spoken to', value: this.supplierContactName || '-' });
        }

        return fields;
    }

    private getPurchaseOrderDetailsPdfFields(relatedPurchaseOrderNumbers: string[]): ICreditRequestPdfField[] {
        const fields: ICreditRequestPdfField[] = [];

        if (relatedPurchaseOrderNumbers.length > 0) {
            fields.push({ label: 'Related PO number(s)', value: relatedPurchaseOrderNumbers.join(', ') });
        }

        fields.push({ label: 'Purchase order required', value: this.yesNo(this.newPurchaseOrderRequired) });

        if (this.newPurchaseOrderRequired === true) {
            fields.push({
                label: 'Purchase order no charge',
                value: this.yesNo(this.newPurchaseOrderNoCharge)
            });
        }

        return fields;
    }

    private getCashCustomerBankDetailsPdfFields(): ICreditRequestPdfField[] {
        if (!this.salesStore.order.isCashSalesAccount) return [];

        return [
            { label: 'Name of Account', value: this.cashCustomerNameOfAccount || '-' },
            { label: 'BSB', value: this.cashCustomerBsb || '-' },
            { label: 'Account Number', value: this.cashCustomerAccountNumber || '-' },
            {
                label: 'Checked verbally with account holder? Y/N',
                value: this.yesNo(this.cashCustomerAccountCheckedVerbally)
            },
            { label: 'By whom / Signature', value: this.cashCustomerAccountCheckedBy || '-' }
        ];
    }

    private getFinancialSummaryPdfFields(
        productReturnValue: number,
        restockingFee: number,
        supplierRestockingFee: number,
        deductedFreightAmount: number,
        calculatedCreditExGst: number,
        totalCreditExGst: number,
        totalCreditInclGst: number,
        amountPaid: number,
        fixedAmountCreditSet: boolean,
        canCalculateReturnAmounts: boolean,
        canShowFinalCreditAmount: boolean
    ): ICreditRequestPdfField[] {
        const { order } = this.salesStore;
        const fields: ICreditRequestPdfField[] = [
            { label: 'Amount paid', value: this.formatCurrency(amountPaid) }
        ];
        const negativeCashCredit = canShowFinalCreditAmount && order.isCashSalesAccount && totalCreditExGst < 0;
        const exceedsPaidAmount = canShowFinalCreditAmount &&
            order.refund.customerCreditExceedsPaid(totalCreditExGst, amountPaid);

        if (order.refund.pickupRequired) {
            fields.push({
                label: 'Product return value (ex GST)',
                value: canCalculateReturnAmounts ? this.formatCurrency(productReturnValue) : '-'
            });
            fields.push({
                label: 'Customer restocking fee (ex GST)',
                value: canCalculateReturnAmounts ? this.formatCurrency(restockingFee) : '-'
            });
        }

        if (this.supplierCreditRequired === true) {
            fields.push({
                label: 'Supplier credit amount (ex GST)',
                value: this.formatCurrency(this.supplierCreditAmount)
            });
        }

        if (this.supplierRestockingFeeRequired === true) {
            fields.push({
                label: 'Supplier restocking fee (ex GST)',
                value: this.formatCurrency(supplierRestockingFee)
            });
        }

        if (this.freightRequired && this.freightChargeMode === 'deduct') {
            fields.push({
                label: 'Freight deducted from credit (ex GST)',
                value: this.formatCurrency(deductedFreightAmount)
            });
        }

        if (fixedAmountCreditSet) {
            fields.push({
                label: 'Calculated credit amount (ex GST)',
                value: canCalculateReturnAmounts ? this.formatCurrency(calculatedCreditExGst) : '-'
            });
            fields.push({
                label: 'Fixed amount credit (ex GST)',
                value: this.formatCurrency(this.fixedAmountCreditExGst || 0)
            });
        }

        fields.push({
            label: 'Total credit amount (ex GST)',
            value: canShowFinalCreditAmount ? this.formatCurrency(totalCreditExGst) : '-'
        });
        fields.push({
            label: 'Total credit amount (incl GST)',
            value: canShowFinalCreditAmount ? this.formatCurrency(totalCreditInclGst) : '-'
        });
        if (negativeCashCredit) {
            fields.push({
                label: 'Credit warning',
                value: 'Amount to be charged to Customer'
            });
        }
        if (exceedsPaidAmount) {
            fields.push({
                label: 'Credit warning',
                value: 'Cannot credit more than the amount paid'
            });
        }

        return fields;
    }

    private getCreditRequestPdfData(): ICreditRequestPdfRequest {
        const { order } = this.salesStore;
        const { refund } = order;
        const returnedLines = this.getReturnedProductLines();
        const productReturnValue = this.getTotalProductReturnValue(returnedLines);
        const restockingFee = this.getTotalRestockingFee(returnedLines);
        const supplierRestockingFee = this.getSupplierRestockingFeeAmount();
        const deductedFreightAmount = this.getDeductedFreightAmount();
        const fixedAmountCreditSet = this.hasFixedAmountCredit();
        const canCalculateReturnAmounts = !this.hasInvalidReturnQuantity();
        const calculatedCreditExGst = canCalculateReturnAmounts
            ? this.getCalculatedCreditAmountExGst(returnedLines)
            : 0;
        const totalCreditExGst = fixedAmountCreditSet
            ? this.getTotalCreditAmountExGst(calculatedCreditExGst)
            : calculatedCreditExGst;
        const canShowFinalCreditAmount = fixedAmountCreditSet || canCalculateReturnAmounts;
        const totalCreditInclGst = canShowFinalCreditAmount
            ? refund.calculateCustomerCreditIncludingGst(totalCreditExGst)
            : 0;
        const amountPaid = this.getAmountPaid();
        const relatedPurchaseOrderNumbers = this.getRelatedPurchaseOrderNumbers(order.orderedProductLines);
        return {
            title: 'Credit Request',
            generatedAt: new Date().toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' }),
            orderNumber: order.orderNumber || this.props.match.params.id,
            customerName: order.customerName || '-',
            orderDetails: [
                { label: 'Order Number', value: order.orderNumber || this.props.match.params.id },
                { label: 'Customer', value: order.customerName || '-' },
                { label: 'Delivered Address', value: order.delivery.address.formattedAddress || '-' },
                { label: 'Delivered Date', value: order.delivery.dateTba ? 'TBA' : dateFormatted(order.delivery.date || null) || '-' }
            ],
            orderSummaryTitle: this.getOrderSummaryPdfTitle(),
            orderSummary: this.getOrderSummaryPdfFields(),
            requestDetails: this.getRequestDetailsPdfFields(returnedLines),
            freightDetails: this.getFreightDetailsPdfFields(),
            supplierDetails: this.getSupplierDetailsPdfFields(),
            purchaseOrderDetails: this.getPurchaseOrderDetailsPdfFields(relatedPurchaseOrderNumbers),
            cashCustomerBankDetails: this.getCashCustomerBankDetailsPdfFields(),
            financialSummary: this.getFinancialSummaryPdfFields(
                productReturnValue,
                restockingFee,
                supplierRestockingFee,
                deductedFreightAmount,
                calculatedCreditExGst,
                totalCreditExGst,
                totalCreditInclGst,
                amountPaid,
                fixedAmountCreditSet,
                canCalculateReturnAmounts,
                canShowFinalCreditAmount
            ),
            productLines: order.orderedProductLines
                .filter(line => line.creditQuantity > 0)
                .map(line => this.getCreditRequestPdfProductLine(line))
        };
    }

    private getCreditRequestPdfProductLine(productLine: ProductLine): ICreditRequestPdfProductLine {
        const validReturnQuantity = this.returnQuantityIsValid(productLine);
        const restockingFee = this.getProductRestockingFee(productLine);
        return {
            code: productLine.code || '-',
            name: productLine.name || '-',
            availableUnits: `${this.formatQuantity(productLine.quantity)} ${productLine.purchaseUnit || 'Units'}`,
            unitPrice: this.formatCurrency(productLine.price),
            returnQuantity: this.formatQuantity(productLine.creditQuantity || 0),
            returnValue: validReturnQuantity ? this.formatCurrency(this.getProductTotalCredited(productLine)) : 'Invalid',
            restockingFeeApplied: restockingFee.applied ? 'Yes' : 'No',
            restockingFeePercentage: restockingFee.applied ? restockingFee.percentage.toString() : '-',
            restockingFeeAmount: restockingFee.applied && validReturnQuantity
                ? this.formatCurrency(this.getRestockingFeeAmount(productLine))
                : '-',
            refundAmount: validReturnQuantity ? this.formatCurrency(this.getProductRefundAmount(productLine)) : 'Invalid',
            purchaseOrderNumber: productLine.purchaseOrderNumber || '-'
        };
    }

    private getCreditRequestPdfFilename(): string {
        const { order } = this.salesStore;
        const orderNumber = order.orderNumber || this.props.match.params.id || 'order';
        const customerName = (order.customerName || 'customer')
            .replace(/\s+/g, '')
            .replace(/[^A-Za-z0-9_-]/g, '');
        return `CreditRequest_${orderNumber}_${customerName}.pdf`;
    }

    private summaryRow(label: string, value: React.ReactNode) {
        return (
            <React.Fragment>
                <Grid item xs={9}><p><strong>{label}</strong></p></Grid>
                <Grid item xs={3} className="number-col"><p>{value}</p></Grid>
            </React.Fragment>
        );
    }

    private renderSummary() {
        const { order } = this.salesStore;
        const { refund } = order;
        const returnedLines = this.getReturnedProductLines();
        const canCalculateReturnAmounts = !this.hasInvalidReturnQuantity();
        const productReturnValue = this.getTotalProductReturnValue(returnedLines);
        const restockingFee = this.getTotalRestockingFee(returnedLines);
        const supplierRestockingFee = this.getSupplierRestockingFeeAmount();
        const deductedFreightAmount = this.getDeductedFreightAmount();
        const fixedAmountCreditSet = this.hasFixedAmountCredit();
        const calculatedCreditExGst = canCalculateReturnAmounts
            ? this.getCalculatedCreditAmountExGst(returnedLines)
            : 0;
        const totalCreditExGst = fixedAmountCreditSet
            ? this.getTotalCreditAmountExGst(calculatedCreditExGst)
            : calculatedCreditExGst;
        const canShowFinalCreditAmount = fixedAmountCreditSet || canCalculateReturnAmounts;
        const totalCreditInclGst = canShowFinalCreditAmount
            ? refund.calculateCustomerCreditIncludingGst(totalCreditExGst)
            : 0;
        const amountPaid = this.getAmountPaid();
        const exceedsPaidAmount = canShowFinalCreditAmount &&
            refund.customerCreditExceedsPaid(totalCreditExGst, amountPaid);
        const negativeCashCreditClass = order.isCashSalesAccount && totalCreditExGst < 0
            ? 'negative-customer-credit'
            : '';
        const creditAmountClass = negativeCashCreditClass || (exceedsPaidAmount ? 'negative-customer-credit' : '');
        const calculatedCurrency = (amount: number) =>
            canCalculateReturnAmounts ? this.formatCurrency(amount) : '—';
        const finalCurrency = (amount: number) =>
            canShowFinalCreditAmount ? this.formatCurrency(amount) : '—';

        return (
            <Grid container className="section">
                <Grid item xs={1} />
                <Grid item xs={10}>
                    <h2>Summary</h2>
                    <Grid container>
                        {this.summaryRow('Reason', this.reason || '—')}

                        {this.summaryRow('Customer credit required', this.yesNo(this.customerCreditRequired))}
                        {this.summaryRow('Product return required', this.yesNo(this.returnRequired))}
                        {refund.pickupRequired && this.summaryRow('Pickup address', refund.fromAddress.formattedAddress || '—')}
                        {refund.pickupRequired && this.summaryRow(
                            'Products to return',
                            returnedLines.length > 0
                                ? returnedLines.map(l => `${l.code} × ${l.creditQuantity}`).join(', ')
                                : '—'
                        )}
                        {this.summaryRow('Freight required', this.yesNo(this.freightRequired))}
                        {this.freightRequired && this.summaryRow('Freight charge to', this.freightChargeTo || '—')}
                        {this.freightRequired && this.summaryRow('Freight Mojoe #', this.freightMojoeNumber || '—')}
                        {this.freightRequired === false && this.summaryRow('No freight reason', this.freightNoReason || '—')}

                        {this.summaryRow('Supplier credit required', this.yesNo(this.supplierCreditRequired))}
                        {this.summaryRow('Supplier restocking fee', this.yesNo(this.supplierRestockingFeeRequired))}
                        {this.summaryRow('Purchase order required', this.yesNo(this.newPurchaseOrderRequired))}
                        {this.newPurchaseOrderRequired === true && this.summaryRow(
                            'Purchase order no charge',
                            this.yesNo(this.newPurchaseOrderNoCharge)
                        )}
                        {this.summaryRow('Supplier contacted', this.yesNo(this.supplierContacted))}
                        {this.supplierContacted === true && this.summaryRow('Supplier', this.supplierName || '—')}
                        {this.supplierContacted === true && this.summaryRow(
                            'Supplier contact spoken to',
                            this.supplierContactName || '—'
                        )}

                        {this.summaryRow('Amount paid', this.formatCurrency(amountPaid))}
                        {refund.pickupRequired && this.summaryRow(
                            'Product return value (ex GST)',
                            calculatedCurrency(productReturnValue)
                        )}
                        {refund.pickupRequired && this.summaryRow(
                            'Customer restocking fee (ex GST)',
                            calculatedCurrency(restockingFee)
                        )}
                        {this.supplierCreditRequired === true && this.summaryRow(
                            'Supplier credit amount (ex GST)',
                            this.formatCurrency(this.supplierCreditAmount)
                        )}
                        {this.supplierRestockingFeeRequired === true && this.summaryRow(
                            'Supplier restocking fee (ex GST)',
                            this.formatCurrency(supplierRestockingFee)
                        )}
                        {this.freightRequired && this.freightChargeMode === 'deduct' && this.summaryRow(
                            'Freight deducted from credit (ex GST)',
                            this.formatCurrency(deductedFreightAmount)
                        )}
                        {fixedAmountCreditSet && this.summaryRow(
                            'Calculated credit amount (ex GST)',
                            calculatedCurrency(calculatedCreditExGst)
                        )}
                        {fixedAmountCreditSet && this.summaryRow(
                            'Fixed amount credit (ex GST)',
                            this.formatCurrency(this.fixedAmountCreditExGst || 0)
                        )}
                        {this.summaryRow('Total credit amount (ex GST)', finalCurrency(totalCreditExGst))}
                        {this.summaryRow(
                            'Total credit amount (incl GST)',
                            <span className={creditAmountClass}>
                                {finalCurrency(totalCreditInclGst)}
                                {negativeCashCreditClass && (
                                    <span className="negative-credit-note">Amount to be charged to Customer</span>
                                )}
                                {exceedsPaidAmount && (
                                    <span className="negative-credit-note">
                                        Cannot credit more than the amount paid
                                    </span>
                                )}
                            </span>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    public render() {
        const { order } = this.salesStore;
        const orderNumber = order.orderNumber || this.props.match.params.id;

        return (
            <React.Fragment>
                <div className="content credit-request-page">
                    <Header title={`Credit Request - ${orderNumber}`} />
                    <Loader area="credit-request" />

                    <ActionsMenu>
                        <ActionItem label="Download PDF" onClick={() => this.downloadCreditRequestPdf()}>
                            <PictureAsPdf />
                        </ActionItem>
                    </ActionsMenu>

                    <Grid container className="section">
                        <Grid item xs={1} />
                        <Grid item xs={10}>
                            <h2>Order Details</h2>
                            <Grid container>
                                <Grid item xs={9}><p><strong>Order Number</strong></p></Grid>
                                <Grid item xs={3} className="number-col"><p>{order.orderNumber}</p></Grid>
                                <Grid item xs={9}><p><strong>Customer</strong></p></Grid>
                                <Grid item xs={3} className="number-col"><p>{order.customerName}</p></Grid>
                                <Grid item xs={9}><p><strong>Delivered Address</strong></p></Grid>
                                <Grid item xs={3} className="number-col"><p>{order.delivery.address.formattedAddress || '—'}</p></Grid>
                                <Grid item xs={9}><p><strong>Delivered Date</strong></p></Grid>
                                <Grid item xs={3} className="number-col">
                                    <p>{order.delivery.dateTba ? 'TBA' : dateFormatted(order.delivery.date || null) || '—'}</p>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    <OrderSummary />

                    {order.refund && (
                        <React.Fragment>
                            <Grid container className="section">
                                <Grid item xs={1} />
                                <Grid item xs={10}>
                                    <h2>Customer Credit</h2>
                                    {this.renderRequiredYesNo(
                                        'Is a credit required to the Customer?',
                                        this.customerCreditRequired,
                                        (checked: boolean) => this.setCustomerCreditRequired(checked)
                                    )}
                                </Grid>
                            </Grid>

                            <Grid container className="section">
                                <Grid item xs={1} />
                                <Grid item xs={10}>
                                    <h2>Return Details</h2>
                                    {this.renderRequiredYesNo(
                                        'Is a return of product required?',
                                        this.returnRequired,
                                        (checked: boolean) => this.setReturnRequired(checked)
                                    )}

                                    {order.refund.pickupRequired && (
                                        <AddressSearchBox
                                            address={order.refund.fromAddress.formattedAddress}
                                            handleAddressSelected={(address: Address) =>
                                                order.refund.update('fromAddress', address)
                                            }
                                            handleChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                order.refund.update('fromAddress', new Address({
                                                    ...order.refund.fromAddress,
                                                    formattedAddress: e.currentTarget.value
                                                }))
                                            }
                                            label="Pickup Address"
                                            fieldName="refundFromAddress"
                                            error={order.refund.fromAddress.errors.formattedAddress}
                                            toggleMap
                                            disabled={false}
                                        >
                                            <AddressMap
                                                forFieldName="refundFromAddress"
                                                moveMarker={true}
                                                lat={order.refund.fromAddress.latLng.lat}
                                                lng={order.refund.fromAddress.latLng.lng}
                                                handleClick={(lat: number, lng: number) => {
                                                    this.orderUiState.setLatLng('refundFromAddress', { lat, lng });
                                                    order.refund.update('fromAddress', new Address({
                                                        ...order.refund.fromAddress,
                                                        latLng: { lat, lng }
                                                    }));
                                                }}
                                            />
                                        </AddressSearchBox>
                                    )}
                                </Grid>
                            </Grid>

                            {order.refund.pickupRequired && (
                                <CreditRequestProductLines
                                    getRestockingFeeApplied={(productLine: ProductLine) =>
                                        this.getProductRestockingFee(productLine).applied
                                    }
                                    getRestockingFeePercentage={(productLine: ProductLine) =>
                                        this.getProductRestockingFee(productLine).percentage
                                    }
                                    getRestockingFeeAmount={(productLine: ProductLine) =>
                                        this.getRestockingFeeAmount(productLine)
                                    }
                                    getReturnValue={(productLine: ProductLine) =>
                                        this.getProductTotalCredited(productLine)
                                    }
                                    getRefundAmount={(productLine: ProductLine) =>
                                        this.getProductRefundAmount(productLine)
                                    }
                                    onRestockingFeeAppliedChange={(productLine: ProductLine, checked: boolean) =>
                                        this.setRestockingFeeApplied(productLine, checked)
                                    }
                                    onRestockingFeePercentageChange={(productLine: ProductLine, percentage: number) =>
                                        this.setRestockingFeePercentage(productLine, percentage)
                                    }
                                />
                            )}

                            <Grid container className="section">
                                <Grid item xs={1} />
                                <Grid item xs={10}>
                                    <h2>Fixed Credit Amount</h2>
                                    <div className="fixed-credit-amount-field">
                                        <CurrencyField
                                            name="fixedAmountCreditExGst"
                                            label="Fixed amount credit (ex GST)"
                                            value={this.fixedAmountCreditExGst}
                                            disabled={false}
                                            onChange={(e: ICurrencyChangedEvent) => {
                                                this.setFixedAmountCreditExGst(e);
                                            }}
                                        />
                                    </div>
                                </Grid>
                            </Grid>

                            <Grid container className="section">
                                <Grid item xs={1} />
                                <Grid item xs={10}>
                                    <h2>Reason</h2>
                                    <TextField
                                        label="What is the reason for credit/return?"
                                        value={this.reason}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            this.setReason(e.currentTarget.value);
                                        }}
                                        multiline
                                        rows={4}
                                        fullWidth
                                        variant="outlined"
                                    />
                                </Grid>
                            </Grid>

                            {order.isCashSalesAccount && (
                                <Grid container className="section">
                                    <Grid item xs={1} />
                                    <Grid item xs={10}>
                                        <h2>Customer Bank Account Details</h2>
                                        <p className="cash-customer-only-note">Cash customer only</p>
                                        <Grid container spacing={16} className="cash-bank-details">
                                            <Grid item xs={12} md={4}>
                                                <TextField
                                                    label="Name of Account"
                                                    value={this.cashCustomerNameOfAccount}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        this.setCashCustomerNameOfAccount(e.currentTarget.value);
                                                    }}
                                                    fullWidth
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <TextField
                                                    label="BSB"
                                                    value={this.cashCustomerBsb}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        this.setCashCustomerBsb(e.currentTarget.value);
                                                    }}
                                                    fullWidth
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <TextField
                                                    label="Account Number"
                                                    value={this.cashCustomerAccountNumber}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        this.setCashCustomerAccountNumber(e.currentTarget.value);
                                                    }}
                                                    fullWidth
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                {this.renderRequiredYesNo(
                                                    'Checked verbally with account holder?',
                                                    this.cashCustomerAccountCheckedVerbally,
                                                    (checked: boolean) =>
                                                        this.setCashCustomerAccountCheckedVerbally(checked)
                                                )}
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    label="By whom / Signature"
                                                    value={this.cashCustomerAccountCheckedBy}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        this.setCashCustomerAccountCheckedBy(e.currentTarget.value);
                                                    }}
                                                    fullWidth
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}

                            <Grid container className="section">
                                <Grid item xs={1} />
                                <Grid item xs={10}>
                                    <h2>Freight</h2>
                                    {this.renderRequiredYesNo(
                                        'Do we need to charge freight?',
                                        this.freightRequired,
                                        (checked: boolean) => this.setFreightRequired(checked)
                                    )}
                                    {this.freightRequired && (
                                        <React.Fragment>
                                            <div className="freight-charge-options">
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={this.freightChargeMode === 'deduct'}
                                                            onChange={() => this.setFreightChargeMode('deduct')}
                                                        />
                                                    }
                                                    label="Take from refund (Cash Customer)"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={this.freightChargeMode === 'separate'}
                                                            onChange={() => this.setFreightChargeMode('separate')}
                                                        />
                                                    }
                                                    label="Charged on separate docket (Account Customer)"
                                                />
                                            </div>
                                            {this.freightChargeMode === 'deduct' && (
                                                <div className="freight-amount-field">
                                                    <CurrencyField
                                                        name="freightAmount"
                                                        label="Amount"
                                                        value={this.freightAmount}
                                                        disabled={false}
                                                        onChange={(e: ICurrencyChangedEvent) => {
                                                            this.setFreightAmount(e.parsedValue);
                                                        }}
                                                    />
                                                </div>
                                            )}
                                            {!!this.freightChargeMode && (
                                                <TextField
                                                    label="Mojoe #"
                                                    value={this.freightMojoeNumber}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        this.setFreightMojoeNumber(e.currentTarget.value);
                                                    }}
                                                    fullWidth
                                                />
                                            )}
                                        </React.Fragment>
                                    )}
                                    {this.freightRequired === false && (
                                        <TextField
                                            label="Reason"
                                            value={this.freightNoReason}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                this.setFreightNoReason(e.currentTarget.value);
                                            }}
                                            multiline
                                            rows={4}
                                            fullWidth
                                            variant="outlined"
                                        />
                                    )}
                                </Grid>
                            </Grid>

                            <Grid container className="section">
                                <Grid item xs={1} />
                                <Grid item xs={10}>
                                    <h2>Supplier Credit</h2>
                                    {this.getRelatedPurchaseOrderNumbers(order.orderedProductLines).length > 0 && (
                                        <div className="related-purchase-orders">
                                            <p><strong>Related PO number(s)</strong></p>
                                            <p>{this.getRelatedPurchaseOrderNumbers(order.orderedProductLines).join(', ')}</p>
                                        </div>
                                    )}
                                    {this.renderRequiredYesNo(
                                        'Is a credit required from Supplier?',
                                        this.supplierCreditRequired,
                                        (checked: boolean) => this.setSupplierCreditRequired(checked)
                                    )}
                                    {this.supplierCreditRequired === true && (
                                        <div className="supplier-credit-amount-field">
                                            <CurrencyField
                                                name="supplierCreditAmount"
                                                label="Credit Amount (ex GST)"
                                                value={this.supplierCreditAmount}
                                                disabled={false}
                                                onChange={(e: ICurrencyChangedEvent) => {
                                                    this.setSupplierCreditAmount(e.parsedValue);
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div className="supplier-restocking-fee">
                                        {this.renderRequiredYesNo(
                                            'Is there a Supplier Restocking Fee?',
                                            this.supplierRestockingFeeRequired,
                                            (checked: boolean) => this.setSupplierRestockingFeeRequired(checked)
                                        )}
                                        {this.supplierRestockingFeeRequired === true && (
                                            <div className="supplier-restocking-fee-amount-field">
                                                <CurrencyField
                                                    name="supplierRestockingFeeAmount"
                                                    label="Supplier Restocking Fee (ex GST)"
                                                    value={this.supplierRestockingFeeAmount}
                                                    disabled={false}
                                                    onChange={(e: ICurrencyChangedEvent) => {
                                                        this.setSupplierRestockingFeeAmount(e.parsedValue);
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </Grid>
                            </Grid>

                            <Grid container className="section">
                                <Grid item xs={1} />
                                <Grid item xs={10}>
                                    <h2>Supplier Contact</h2>
                                    {this.renderRequiredYesNo(
                                        'Has the supplier been contacted?',
                                        this.supplierContacted,
                                        (checked: boolean) => this.setSupplierContacted(checked)
                                    )}
                                    {this.supplierContacted === true && (
                                        <React.Fragment>
                                            <TextField
                                                label="Supplier"
                                                value={this.supplierName}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    this.setSupplierName(e.currentTarget.value);
                                                }}
                                                fullWidth
                                            />
                                            <TextField
                                                label="Supplier Contact Name"
                                                value={this.supplierContactName}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    this.setSupplierContactName(e.currentTarget.value);
                                                }}
                                                fullWidth
                                            />
                                        </React.Fragment>
                                    )}
                                </Grid>
                            </Grid>

                            <Grid container className="section">
                                <Grid item xs={1} />
                                <Grid item xs={10}>
                                    <h2>Purchase Order</h2>
                                    {this.renderRequiredYesNo(
                                        'Do we need to raise a new purchase order?',
                                        this.newPurchaseOrderRequired,
                                        (checked: boolean) => this.setNewPurchaseOrderRequired(checked)
                                    )}
                                    {this.newPurchaseOrderRequired === true && (
                                        this.renderRequiredYesNo(
                                            'Is it at no charge?',
                                            this.newPurchaseOrderNoCharge,
                                            (checked: boolean) => this.setNewPurchaseOrderNoCharge(checked)
                                        )
                                    )}
                                </Grid>
                            </Grid>

                            {this.renderSummary()}
                        </React.Fragment>
                    )}
                </div>
            </React.Fragment>
        );
    }
}

export default CreditRequest;
